import { createAction, createSlice } from "@reduxjs/toolkit";

import { pollCart } from "@app/modules/checkout/store/v2/thunks/cart";
import type { CartPollResponse } from "@app/modules/checkout/types/cart-v2";
import {
  deleteSelectedSeatsFromTripItemPassengers,
  isTripItem
} from "@app/modules/checkout/utils/cart-v2";
import type { NapiV2Cart, NapiV2TripItem } from "@app/types/napi-v2-cart-types";
import type {
  NapiV2Answer,
  NapiV2CartPassenger
} from "@app/types/napi-v2-passengers-types";
import type { PassengerSeatSelectionInstance } from "@app/types/seat-selection-types";
import { mergeArraysReplacingByKey } from "@app/utils/array";

import type { ActionCreatorWithOptionalPayload } from "@reduxjs/toolkit";

const initialState = {} as NapiV2Cart;

export const CART_V2 = {
  POLL_BEGIN: "cart/poll/begin",
  POLL_SUCCESS: "cart/poll/success",
  POLL_FAILURE: "cart/poll/failure",
  SELECTED_SEATS_SET: "cart/selected_seats/set",
  SELECTED_SEATS_DELETE: "cart/selected_seats/delete"
} as const;

export interface SetSelectedSeatsAction {
  segment_id: string;
  passengers: PassengerSeatSelectionInstance[];
}

interface DeleteSelectedSeatsAction {
  seats: string[];
}

export const deleteSelectedSeats = createAction<DeleteSelectedSeatsAction>(
  CART_V2.SELECTED_SEATS_DELETE
);

export const setSelectedSeats = createAction<
  SetSelectedSeatsAction,
  typeof CART_V2.SELECTED_SEATS_SET
>(CART_V2.SELECTED_SEATS_SET);

export const pollCartV2Begin: ActionCreatorWithOptionalPayload<
  undefined,
  "cart/poll/begin"
> = createAction<undefined, typeof CART_V2.POLL_BEGIN>(CART_V2.POLL_BEGIN);

export const pollCartV2Success = createAction<
  NapiV2Cart,
  typeof CART_V2.POLL_SUCCESS
>(CART_V2.POLL_SUCCESS);

export const pollCartV2Failure = createAction<
  Error,
  typeof CART_V2.POLL_FAILURE
>(CART_V2.POLL_FAILURE);

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(pollCartV2Success, cartV2PollSuccess)
      .addCase(pollCart.fulfilled, cartV2PollSuccess)
      .addCase(setSelectedSeats, (state, action) => {
        const trip_item = state.items.find(isTripItem);
        if (!trip_item) {
          return state;
        }

        const prepared_passengers = action.payload.passengers.map(
          (passenger, index) => {
            const answers_array = Object.entries(passenger.questions).map(
              entry => {
                return { question_key: entry[0], value: entry[1] };
              }
            ) as NapiV2Answer[];

            const answers_to_merge =
              answers_array.length > 0 ? answers_array : null;

            // @ts-expect-error TS2322 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
            const curr_passenger: NapiV2CartPassenger =
              trip_item.passengers[index];

            return {
              ...curr_passenger,
              answers: answers_to_merge
                ? mergeArraysReplacingByKey(
                    curr_passenger.answers || [],
                    answers_to_merge,
                    "question_key"
                  )
                : curr_passenger.answers,
              selected_seats: passenger.selected_seats
            };
          }
        );

        trip_item.passengers = prepared_passengers;
      })
      .addCase(deleteSelectedSeats, (state, { payload: { seats } }) => {
        const trip_item = state.items.find(isTripItem);
        if (!trip_item) {
          return state;
        }

        trip_item.passengers = deleteSelectedSeatsFromTripItemPassengers(
          trip_item.passengers,
          seats
        );
      });
  }
});

const cartV2PollSuccess = (
  state: NapiV2Cart,
  action: {
    payload: CartPollResponse | NapiV2Cart | undefined;
  }
) => {
  if (!action.payload) {
    return state;
  }

  const curr_trip_item = state.items.find(
    item => item.type === "trip" && item.id
  ) as NapiV2TripItem;

  const curr_trip_has_selected_seats = curr_trip_item.passengers.some(
    passenger => passenger.selected_seats && passenger.selected_seats.length > 0
  );

  // The selected seats might not be persisted, in case it doesn't have price per seat. In that case, if there are no selected seats, we can just return the new cart
  if (!curr_trip_has_selected_seats) {
    return action.payload;
  }

  // However, if the passengers have selected seats, we need to merge the answers and selected seats from the current trip otherwise they will be lost
  const new_items = action.payload.items.filter(
    item => item.type !== "trip" || item.id !== curr_trip_item.id
  );

  const new_trip_item = action.payload.items.find(
    item => item.type === "trip" && item.id === curr_trip_item.id
  ) as NapiV2TripItem;

  const prepared_passengers = new_trip_item.passengers.map(
    (passenger, index) => {
      const answers_array = passenger.answers || [];

      const answers_to_merge = answers_array.length > 0 ? answers_array : null;

      // @ts-expect-error TS2322 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      const curr_passenger: NapiV2CartPassenger =
        curr_trip_item.passengers[index];

      return {
        ...curr_passenger,
        answers: answers_to_merge
          ? mergeArraysReplacingByKey(
              curr_passenger.answers || [],
              answers_to_merge,
              "question_key"
            )
          : curr_passenger.answers,
        selected_seats: curr_passenger.selected_seats
      };
    }
  );

  new_trip_item.passengers = prepared_passengers;

  const new_cart = {
    ...action.payload,
    items: [...new_items, new_trip_item]
  };
  return new_cart;
};

export default cartSlice.reducer;
